import { useCallback, useEffect, useMemo } from 'react';

import { submitContact } from '@/components/forms/ContactForm/submit';
import { track } from '../analytics/events';
import { ERROR_CONTACT_MESSAGE, SUCCESS_CONTACT_MESSAGE } from '../config/system';
import type { TContactFormData } from '../interfaces/forms/contactForm.types';
import type { TUseFormOptions } from '../interfaces/hooks/useForm.types';
import toaster from '../packages/toaster';
import contactSchema from '../packages/yup';
import useForm from './useForm';

const defaultValues: TContactFormData = {
  firstName: '',
  lastName: '',
  company: '',
  mobile: '',
  email: '',
  message: '',
  roles: [],
};

const focusOrder: (keyof TContactFormData)[] = [
  'firstName',
  'lastName',
  'company',
  'roles',
  'mobile',
  'email',
  'message',
];

const useContactForm = () => {
  const onSubmit = useCallback(async (data: TContactFormData) => {
    const startedAt = performance.now?.() ?? Date.now();
    try {
      await submitContact(data);
      const _time_to_submit_ms = (performance.now?.() ?? Date.now()) - startedAt;
      // Generate lead (custom event). Enhanced Measurement handles form_*; we only send generate_lead.
      track.lead({ form_id: 'contact', form_name: 'Contact form', method: 'contact_form' });
      return toaster(SUCCESS_CONTACT_MESSAGE);
    } catch {
      return toaster(ERROR_CONTACT_MESSAGE);
    }
  }, []);

  const config: TUseFormOptions<TContactFormData> = useMemo(() => {
    return {
      onSubmit,
      defaultValues,
      schema: contactSchema,
      shouldFocusError: false,
    };
  }, [onSubmit]);

  const { form, handleSubmit } = useForm<TContactFormData>(config);
  const { errors, submitCount } = form.formState;

  useEffect(() => {
    if (!submitCount || !errors) return;
    const target = focusOrder.find((field) => errors[field]);
    if (!target) return;
    const frame = requestAnimationFrame(() => form.setFocus(target));
    return () => cancelAnimationFrame(frame);
  }, [errors, submitCount, form]);

  return { form, handleSubmit };
};

export default useContactForm;
